import { HANDOFF_LABELS } from '../../components/passport/PassportPortfolio';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiGet, apiPut, apiDelete } from '../../contexts/AuthContext';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, FileText, Building2, User, Calendar, Trash2, ExternalLink, Filter, ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';

import { APPLICATION_STATUSES as STATUSES, APP_STATUS_STYLES as STATUS_STYLES } from '../../components/WorkflowTracker';

export default function Applications() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [handoffFilter, setHandoffFilter] = useState('all');
  // Status is filtered here, not on the server, so every status pill keeps its count.
  const shown = statusFilter === 'all' ? applications : applications.filter(a => a.status === statusFilter);
  const [updatingId, setUpdatingId] = useState(null);

  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Wait for typing to pause before searching.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchApplications = useCallback((signal) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (handoffFilter !== 'all') params.set('handoff_state', handoffFilter);
    const qs = params.toString();
    apiGet(`/applications${qs ? '?' + qs : ''}`, { signal })
      .then(r => setApplications(r.data))
      .catch(err => { if (err.code !== 'ERR_CANCELED') toast.error('Failed to load applications'); })
      .finally(() => { if (!signal?.aborted) setLoading(false); });
  }, [debouncedSearch, handoffFilter]);

  // A newer search cancels the older request, so a slow stale reply can't overwrite the list.
  useEffect(() => {
    const controller = new AbortController();
    fetchApplications(controller.signal);
    return () => controller.abort();
  }, [fetchApplications]);

  const handleStatusChange = async (appId, newStatus) => {
    setUpdatingId(appId);
    try {
      await apiPut(`/applications/${appId}`, { status: newStatus });
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, status: newStatus } : a));
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
    finally { setUpdatingId(null); }
  };

  const handleDelete = async (appId, appNum) => {
    if (!window.confirm(`Delete application ${appNum}?`)) return;
    try {
      await apiDelete(`/applications/${appId}`);
      setApplications(prev => prev.filter(a => a.id !== appId));
      toast.success('Application deleted');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-ember tracking-tight" style={{ fontFamily: 'Outfit' }}>Applications</h1>
            <p className="text-ink-2 mt-1">Track all university applications across candidates</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="px-3 py-1.5 bg-surface-2 text-brand rounded-full text-sm font-semibold">
            {applications.length} total
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by candidate, university, app number..."
            className="pl-10 h-11 border-contrast/10" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 h-11" aria-label="Filter by status">
            <Filter className="w-4 h-4 mr-2 text-ink-3" />
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={handoffFilter} onValueChange={setHandoffFilter}>
          <SelectTrigger className="w-full sm:w-56 h-11" aria-label="Filter by handoff">
            <ArrowLeftRight className="w-4 h-4 mr-2 text-ink-3" />
            <SelectValue placeholder="All handoffs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All handoffs</SelectItem>
            <SelectItem value="preparing">Preparing with partner</SelectItem>
            <SelectItem value="with_team">With XpertAdvisor</SelectItem>
            <SelectItem value="returned">Needs partner input</SelectItem>
            <SelectItem value="untracked">No handoff recorded</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Status summary pills */}
      <div className="flex flex-wrap gap-2">
        {STATUSES.map(s => {
          const count = applications.filter(a => a.status === s).length;
          if (!count) return null;
          return (
            <button key={s}
              onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${STATUS_STYLES[s]} ${statusFilter === s ? 'ring-2 ring-offset-1 ring-offset-canvas ring-brand' : ''}`}>
              {s} ({count})
            </button>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" />
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-surface/70 backdrop-blur-xl rounded-2xl border border-contrast/10 p-16 text-center">
          <FileText className="w-12 h-12 text-ink-3 mx-auto mb-4" />
          <p className="text-ink-2 font-medium">No applications found</p>
          <p className="text-ink-3 text-sm mt-1">Applications are created from a candidate's profile</p>
        </div>
      ) : (
        <div className="bg-surface/70 backdrop-blur-xl rounded-2xl border border-contrast/10 overflow-x-auto" role="region" aria-label="Applications table" tabIndex={0}>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-contrast/5 border-b border-contrast/10">
                <th className="text-left px-5 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wide">App #</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wide">Candidate</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wide">University</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wide">Partner</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wide">Date</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wide">Application status</th>
                <th className="px-5 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((app, idx) => (
                <tr key={app.id} className={`border-b border-contrast/[0.07] hover:bg-contrast/5 transition-colors ${idx === shown.length - 1 ? 'border-0' : ''}`}>
                  <td className="px-5 py-3.5">
                    <Link
                      to={`/admin/applications/${app.id}`}
                      className="whitespace-nowrap font-mono text-xs font-semibold text-brand bg-surface-2 px-2 py-1 rounded hover:bg-surface-3 transition-colors">
                      {app.application_number}
                    </Link>
                  </td>
                  <td className="px-5 py-3.5">
                    <button
                      onClick={() => navigate(`/admin/candidates/${app.candidate_id}`)}
                      className="flex items-center gap-2 group text-left">
                      <div className="w-7 h-7 shrink-0 rounded-full bg-surface-3 flex items-center justify-center">
                        <User className="w-3.5 h-3.5 text-brand" />
                      </div>
                      <span className="font-medium text-ink group-hover:text-brand transition-colors">
                        {app.candidate_name}
                      </span>
                      <ExternalLink className="w-3 h-3 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5 text-ink-3 shrink-0" />
                      <div>
                        <p className="font-medium text-ink leading-tight">{app.university_name}</p>
                        <p className="text-xs text-ink-3">{app.university_country}</p>
                        {(app.course_name || app.intake) && (
                          <p className="text-xs text-brand mt-0.5">
                            {[app.course_name, app.intake].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-ink-2 min-w-[9rem]">{app.partner_name || '—'}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1.5 text-ink-2 whitespace-nowrap">
                      <Calendar className="w-3.5 h-3.5 shrink-0" />
                      {new Date(app.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="w-48 space-y-2" data-testid={`application-status-${app.id}`} aria-busy={updatingId === app.id}>
                      <Select
                        value={app.status}
                        disabled={updatingId === app.id}
                        onValueChange={value => handleStatusChange(app.id, value)}>
                        <SelectTrigger
                          aria-label={`Application status for ${app.application_number}`}
                          className={`h-9 w-full rounded-lg px-3 text-xs font-medium shadow-none ${STATUS_STYLES[app.status] || STATUS_STYLES.Inactive}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className={`text-xs leading-relaxed ${app.handoff_state === 'returned' ? 'text-ember' : 'text-ink-2'}`}>
                        <span className="text-ink-3">Handoff: </span>{HANDOFF_LABELS[app.handoff_state || 'untracked'] || HANDOFF_LABELS.untracked}
                      </p>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <button onClick={() => handleDelete(app.id, app.application_number)}
                      aria-label={`Delete application ${app.application_number}`}
                      className="p-1.5 text-ink-3 hover:text-danger hover:bg-ember/10 rounded-lg transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
